import React, { useEffect, useState, useCallback, useMemo } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Plus, Pencil, Trash2, X, LayoutGrid, List, User, Building2, FileText, Calendar, Briefcase, LogOut, Download, QrCode, UserPlus, Search, CheckCircle2, AlertCircle } from 'lucide-react';
import { QRCodeCanvas } from 'qrcode.react';
import { useAuth } from '../../context/AuthContext';

const API = process.env.REACT_APP_BACKEND_URL + '/api';

/**
 * Deriva el estado visual efectivo de una habitación combinando su estado maestro
 * y si tiene ocupantes activos.
 */
function estadoEfectivo(h) {
  if (h.estado === 'deshabilitada') return 'deshabilitada';
  if (h.estado === 'mantenimiento') return 'mantenimiento';
  if (h.estado === 'aseo') return 'aseo';
  if ((h.ocupantes_actuales || 0) > 0) return 'ocupada';
  return 'disponible';
}

const ESTADO_META = {
  disponible:    { label: 'Disponible',    dot: 'bg-emerald-500', card: 'bg-emerald-50 border-emerald-300 text-emerald-900 hover:bg-emerald-100', badge: 'bg-emerald-100 text-emerald-700' },
  ocupada:       { label: 'Ocupada',       dot: 'bg-red-500',     card: 'bg-red-50 border-red-300 text-red-900 hover:bg-red-100',             badge: 'bg-red-100 text-red-700' },
  aseo:          { label: 'En aseo',       dot: 'bg-amber-400',   card: 'bg-amber-50 border-amber-300 text-amber-900 hover:bg-amber-100',      badge: 'bg-amber-100 text-amber-800' },
  mantenimiento: { label: 'Mantenimiento', dot: 'bg-orange-500',  card: 'bg-orange-50 border-orange-300 text-orange-900 hover:bg-orange-100',  badge: 'bg-orange-100 text-orange-700' },
  deshabilitada: { label: 'Deshabilitada', dot: 'bg-slate-400',   card: 'bg-slate-100 border-slate-300 text-slate-500 hover:bg-slate-200',    badge: 'bg-slate-200 text-slate-600' },
};

export default function SgHabitaciones() {
  const { token } = useAuth();
  const headers = { Authorization: `Bearer ${token}` };
  const [rows, setRows] = useState([]);
  const [modal, setModal] = useState(null);
  const [ocupanteHab, setOcupanteHab] = useState(null); // habitación seleccionada para ver ocupante
  const [asignarHab, setAsignarHab] = useState(null); // habitación libre para asignar trabajador
  const [estadoInfoHab, setEstadoInfoHab] = useState(null); // habitación en aseo/mantenimiento/deshabilitada
  const [vista, setVista] = useState('mapa'); // 'mapa' | 'lista'

  const load = useCallback(async () => {
    try {
      const r = await axios.get(`${API}/servicios-generales/habitaciones`, { headers });
      setRows(r.data);
    } catch (_) { /* silencioso */ }
  }, [token]);

  useEffect(() => { load(); }, [load]);

  const del = async (h) => {
    if (!window.confirm(`Eliminar habitación ${h.numero}?`)) return;
    try {
      await axios.delete(`${API}/servicios-generales/habitaciones/${h.id}`, { headers });
      toast.success('Eliminada.'); load();
    } catch (e) { toast.error(e.response?.data?.detail || 'Error'); }
  };

  // Conteos por estado y edificios
  const stats = useMemo(() => {
    const s = { disponible: 0, ocupada: 0, aseo: 0, mantenimiento: 0, deshabilitada: 0, total: rows.length };
    rows.forEach(h => { s[estadoEfectivo(h)] += 1; });
    // % ocupación sobre habitaciones "activas" (excluye deshabilitadas/mantención/aseo)
    const denom = s.disponible + s.ocupada;
    s.ocupacion_pct = denom > 0 ? Math.round((s.ocupada / denom) * 100) : 0;
    return s;
  }, [rows]);

  const edificios = useMemo(() => {
    const map = new Map();
    rows.forEach(h => {
      const key = h.edificio?.trim() || 'Sin edificio';
      if (!map.has(key)) map.set(key, []);
      map.get(key).push(h);
    });
    // Ordenar habitaciones por número dentro de cada edificio
    for (const arr of map.values()) {
      arr.sort((a, b) => String(a.numero).localeCompare(String(b.numero), 'es', { numeric: true }));
    }
    // Ordenar edificios alfabéticamente
    return Array.from(map.entries()).sort((a, b) => a[0].localeCompare(b[0], 'es'));
  }, [rows]);

  return (
    <div className="space-y-4" data-testid="sg-habitaciones-page">
      <div className="flex items-center justify-between gap-3 flex-wrap">
        <div>
          <h1 className="text-2xl font-bold text-slate-900">Habitaciones</h1>
          <p className="text-sm text-slate-500">Mantenedor de habitaciones con estado y mapa visual.</p>
        </div>
        <div className="flex items-center gap-2">
          {/* Toggle vista */}
          <div className="inline-flex rounded-lg border border-slate-200 bg-white p-0.5">
            <button
              onClick={() => setVista('mapa')}
              className={`inline-flex items-center gap-1.5 px-3 py-1.5 text-xs font-semibold rounded-md transition-colors ${vista === 'mapa' ? 'bg-emerald-700 text-white shadow' : 'text-slate-600 hover:bg-slate-100'}`}
              data-testid="btn-vista-mapa">
              <LayoutGrid className="w-3.5 h-3.5" /> Mapa
            </button>
            <button
              onClick={() => setVista('lista')}
              className={`inline-flex items-center gap-1.5 px-3 py-1.5 text-xs font-semibold rounded-md transition-colors ${vista === 'lista' ? 'bg-emerald-700 text-white shadow' : 'text-slate-600 hover:bg-slate-100'}`}
              data-testid="btn-vista-lista">
              <List className="w-3.5 h-3.5" /> Lista
            </button>
          </div>
        </div>
      </div>

      {/* KPIs por estado */}
      <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-2">
        <KpiBox label="Total" value={stats.total} tone="bg-slate-100 text-slate-700 border-slate-200" />
        <KpiBox label="% Ocupación" value={`${stats.ocupacion_pct}%`} tone="bg-sky-50 text-sky-800 border-sky-200" dot="bg-sky-500" />
        <KpiBox label="Ocupadas" value={stats.ocupada} tone="bg-red-50 text-red-800 border-red-200" dot="bg-red-500" />
        <KpiBox label="Disponibles" value={stats.disponible} tone="bg-emerald-50 text-emerald-800 border-emerald-200" dot="bg-emerald-500" />
        <KpiBox label="En aseo" value={stats.aseo} tone="bg-amber-50 text-amber-800 border-amber-200" dot="bg-amber-400" />
        <KpiBox label="Mantenimiento" value={stats.mantenimiento} tone="bg-orange-50 text-orange-800 border-orange-200" dot="bg-orange-500" />
      </div>

      {rows.length === 0 ? (
        <div className="bg-white rounded-2xl border border-slate-200 py-16 text-center">
          <p className="text-slate-400 text-sm mb-3">Sin habitaciones registradas.</p>
          <p className="text-xs text-slate-400">Ve a <b>Mantenedores &rarr; Pabellones</b> para crear tus pabellones y sus habitaciones.</p>
        </div>
      ) : vista === 'mapa' ? (
        <MapaHabitaciones
          edificios={edificios}
          onSeleccionar={(h) => {
            if ((h.ocupantes_actuales || 0) > 0) setOcupanteHab(h);
            else if (h.estado === 'activa') setAsignarHab(h);
            else setEstadoInfoHab(h);
          }}
        />
      ) : (
        <TablaHabitaciones rows={rows} onEditar={(h) => setModal({ mode: 'edit', data: h })} onEliminar={del} />
      )}

      {modal && <ModalHab modal={modal} onClose={() => setModal(null)} onSaved={() => { setModal(null); load(); }} headers={headers} />}
      {asignarHab && (
        <ModalAsignarTrabajador
          hab={asignarHab}
          headers={headers}
          onClose={() => setAsignarHab(null)}
          onSaved={() => { setAsignarHab(null); load(); }}
        />
      )}
      {estadoInfoHab && (
        <ModalEstadoInfo
          hab={estadoInfoHab}
          onClose={() => setEstadoInfoHab(null)}
        />
      )}
      {ocupanteHab && (
        <ModalOcupante
          hab={ocupanteHab}
          onClose={() => setOcupanteHab(null)}
          onLiberar={async (h) => {
            if (!window.confirm(`¿Liberar habitación ${h.numero}? Se cerrará la asignación activa.`)) return;
            try {
              // buscar la asignación activa y liberarla
              const asig = await axios.get(`${API}/servicios-generales/hoteleria/asignaciones?activas=true`, { headers });
              const target = (asig.data || []).find(a => a.habitacion_id === h.id);
              if (!target) { toast.error('No se encontró asignación activa'); return; }
              await axios.post(`${API}/servicios-generales/hoteleria/asignaciones/${target.id}/liberar`, {}, { headers });
              toast.success('Habitación liberada.');
              setOcupanteHab(null);
              load();
            } catch (e) { toast.error(e.response?.data?.detail || 'Error al liberar'); }
          }}
        />
      )}
    </div>
  );
}

function KpiBox({ label, value, tone, dot }) {
  return (
    <div className={`px-3 py-2 rounded-lg border ${tone} flex items-center gap-2`}>
      {dot && <span className={`w-2.5 h-2.5 rounded-full ${dot}`} />}
      <div>
        <div className="text-[10px] font-bold uppercase tracking-wide opacity-80">{label}</div>
        <div className="text-lg font-black leading-none">{value}</div>
      </div>
    </div>
  );
}

function MapaHabitaciones({ edificios, onSeleccionar }) {
  return (
    <div className="space-y-6">
      {edificios.map(([nombre, habs]) => (
        <div key={nombre} className="bg-white rounded-2xl border border-slate-200 p-4">
          <div className="flex items-center justify-between mb-3">
            <h3 className="text-sm font-bold text-emerald-900 tracking-tight">{nombre}</h3>
            <span className="text-xs text-slate-500">{habs.length} habitaciones</span>
          </div>
          <div className="grid grid-cols-4 sm:grid-cols-6 md:grid-cols-8 lg:grid-cols-10 gap-2" data-testid={`sg-mapa-${nombre}`}>
            {habs.map(h => {
              const est = estadoEfectivo(h);
              const meta = ESTADO_META[est];
              const ocu = h.ocupante;
              const opAseoMant = (est === 'aseo' || est === 'mantenimiento') ? h.estado_operador : null;
              const tooltip = ocu
                ? `${h.numero} · ${meta.label}\n${ocu.nombre || ocu.rut || ''}${ocu.es_lomas ? '\nGerencia: ' + (ocu.gerencia || '—') : '\n' + (ocu.empresa_nombre || '—') + (ocu.contrato ? '\nContrato: ' + ocu.contrato : '')}`
                : opAseoMant
                  ? `${h.numero} · ${meta.label}\n${est === 'aseo' ? 'A cargo:' : 'Técnico:'} ${opAseoMant}`
                  : `${h.numero} · ${meta.label}${h.tipo ? ' · ' + h.tipo : ''}`;
              return (
                <button
                  key={h.id}
                  onClick={() => onSeleccionar(h)}
                  title={tooltip}
                  className={`relative aspect-square rounded-lg border-2 flex flex-col items-center justify-center transition-all duration-150 ${meta.card} focus:outline-none focus:ring-2 focus:ring-emerald-400`}
                  data-testid={`sg-hab-${h.numero}`}
                >
                  <span className={`absolute top-1.5 right-1.5 w-2 h-2 rounded-full ${meta.dot}`} />
                  <span className="text-sm font-black leading-none">{h.numero}</span>
                  <span className="text-[9px] mt-1 font-semibold uppercase tracking-wide opacity-75">{meta.label}</span>
                  {ocu && (
                    <span className="mt-0.5 flex items-center gap-0.5 text-[9px] font-semibold opacity-85 truncate max-w-full px-1">
                      <User className="w-2.5 h-2.5 shrink-0" />
                      <span className="truncate">{(ocu.nombre || ocu.rut || '').split(' ').slice(0, 2).join(' ')}</span>
                    </span>
                  )}
                  {!ocu && opAseoMant && (
                    <span className="mt-0.5 flex items-center gap-0.5 text-[9px] font-semibold opacity-85 truncate max-w-full px-1">
                      <User className="w-2.5 h-2.5 shrink-0" />
                      <span className="truncate">{opAseoMant.split(' ').slice(0, 2).join(' ')}</span>
                    </span>
                  )}
                </button>
              );
            })}
          </div>
        </div>
      ))}

      {/* Leyenda */}
      <div className="bg-white rounded-2xl border border-slate-200 p-4">
        <div className="text-[11px] font-bold uppercase tracking-wide text-slate-500 mb-2">Leyenda</div>
        <div className="flex flex-wrap gap-3">
          {Object.entries(ESTADO_META).map(([k, m]) => (
            <div key={k} className="flex items-center gap-1.5 text-xs text-slate-700">
              <span className={`w-3 h-3 rounded-sm ${m.dot}`} />
              <span>{m.label}</span>
            </div>
          ))}
        </div>
        <p className="text-[10px] text-slate-400 mt-2">
          Tip: click en <b className="text-emerald-700">verde</b> para asignar un trabajador · click en <b className="text-red-700">roja</b> para ver al ocupante · click en <b className="text-amber-700">aseo / mantención</b> para ver quién la atiende.
        </p>
      </div>
    </div>
  );
}

function ModalOcupante({ hab, onClose, onLiberar }) {
  const ocu = hab.ocupante || {};
  const esLomas = ocu.es_lomas;
  const [showQr, setShowQr] = useState(false);
  const qrUrl = `${window.location.origin}/sg-qr/${encodeURIComponent(hab.numero)}`;

  // Fecha objetivo del temporizador: check-out planificado (fin del uso de la habilitación)
  const targetDateStr = ocu.check_out;
  const targetLabel = ocu.check_out ? 'Check-out' : null;

  const descargarQr = () => {
    const canvas = document.getElementById('qr-ocu-canvas');
    if (!canvas) return;
    const a = document.createElement('a');
    a.href = canvas.toDataURL('image/png');
    a.download = `QR-Habitacion-${hab.numero}.png`;
    a.click();
  };

  return (
    <div className="fixed inset-0 z-50 bg-black/50 flex items-center justify-center p-4 overflow-y-auto" onClick={onClose}>
      <div className="bg-white rounded-2xl w-full max-w-md overflow-hidden shadow-2xl my-8" onClick={e => e.stopPropagation()} data-testid="sg-ocupante-modal">
        <div className="bg-gradient-to-r from-red-600 to-rose-600 text-white px-5 py-4 flex items-center justify-between">
          <div>
            <div className="text-[11px] font-semibold uppercase tracking-wider opacity-90">Habitación ocupada</div>
            <div className="text-2xl font-black leading-tight">{hab.numero}</div>
            <div className="text-xs opacity-90">{hab.edificio || 'Sin edificio'} · {hab.tipo || 'Sin tipo'}</div>
          </div>
          <button onClick={onClose} className="p-1 rounded hover:bg-white/20"><X className="w-5 h-5" /></button>
        </div>

        {/* Temporizador */}
        {targetDateStr && targetLabel && (
          <Countdown targetDate={targetDateStr} label={targetLabel} />
        )}

        <div className="p-5 space-y-3">
          <div>
            <div className="text-[10px] font-bold uppercase tracking-wide text-slate-400 mb-0.5">Ocupante</div>
            <div className="text-lg font-bold text-slate-900 flex items-center gap-2">
              <User className="w-4 h-4 text-emerald-700" />
              {ocu.nombre || '—'}
            </div>
            {ocu.rut && <div className="text-xs text-slate-500 ml-6">RUT: {ocu.rut}</div>}
          </div>

          {esLomas ? (
            <div className="rounded-lg bg-emerald-50 border border-emerald-200 p-3 space-y-2">
              <div className="flex items-center gap-2 text-emerald-900 text-xs font-bold uppercase tracking-wide">
                <Building2 className="w-3.5 h-3.5" /> Trabajador Lomas Bayas
              </div>
              <InfoRow icon={Briefcase} label="Gerencia" value={ocu.gerencia || '—'} />
              {ocu.superintendencia && <InfoRow icon={Briefcase} label="Superintendencia" value={ocu.superintendencia} />}
              {ocu.cargo && <InfoRow icon={Briefcase} label="Cargo" value={ocu.cargo} />}
            </div>
          ) : (
            <div className="rounded-lg bg-sky-50 border border-sky-200 p-3 space-y-2">
              <div className="flex items-center gap-2 text-sky-900 text-xs font-bold uppercase tracking-wide">
                <Building2 className="w-3.5 h-3.5" /> Contratista
              </div>
              <InfoRow icon={Building2} label="Empresa" value={ocu.empresa_nombre || '—'} />
              <InfoRow icon={FileText} label="Contrato" value={ocu.contrato ? ocu.contrato : 'Sin contrato vigente'} />
              {ocu.contrato_descripcion && (
                <div className="text-[11px] text-slate-500 ml-5 -mt-1 italic truncate">{ocu.contrato_descripcion}</div>
              )}
              {ocu.contrato_fecha_termino && (
                <InfoRow icon={Calendar} label="Vence" value={ocu.contrato_fecha_termino.split('-').reverse().join('-')} />
              )}
              {ocu.cargo && <InfoRow icon={Briefcase} label="Cargo" value={ocu.cargo} />}
            </div>
          )}

          {(ocu.check_in || ocu.check_out) && (
            <div className="grid grid-cols-2 gap-2 pt-1">
              {ocu.check_in && <InfoRow icon={Calendar} label="Check-in" value={ocu.check_in.split('-').reverse().join('-')} />}
              {ocu.check_out && <InfoRow icon={Calendar} label="Check-out" value={ocu.check_out.split('-').reverse().join('-')} />}
            </div>
          )}

          {/* QR de la habitación */}
          <div className="pt-2 border-t border-slate-100">
            <button type="button" onClick={() => setShowQr(v => !v)}
              className="inline-flex items-center gap-2 text-sm font-semibold text-emerald-800 hover:text-emerald-900"
              data-testid="btn-ver-qr-ocupada">
              <QrCode className="w-4 h-4" /> {showQr ? 'Ocultar' : 'Ver'} código QR de la habitación
            </button>
            {showQr && (
              <div className="mt-3 flex flex-col items-center bg-slate-50 border border-slate-200 rounded-xl p-3">
                <div className="bg-white p-3 rounded-lg shadow-sm border border-slate-200">
                  <QRCodeCanvas
                    id="qr-ocu-canvas"
                    value={qrUrl}
                    size={180}
                    level="H"
                    includeMargin={false}
                  />
                </div>
                <div className="text-[10px] text-slate-500 mt-2 break-all text-center max-w-full px-2">{qrUrl}</div>
                <button type="button" onClick={descargarQr}
                  className="inline-flex items-center gap-1.5 mt-2 px-3 py-1.5 bg-emerald-700 hover:bg-emerald-800 text-white text-xs font-semibold rounded-lg"
                  data-testid="btn-descargar-qr-ocupada">
                  <Download className="w-3.5 h-3.5" /> Descargar PNG
                </button>
              </div>
            )}
          </div>
        </div>
        <div className="px-5 py-3 bg-slate-50 border-t border-slate-200 flex justify-end items-center">
          <button onClick={() => onLiberar(hab)}
            className="inline-flex items-center gap-1.5 px-3 py-1.5 bg-red-600 hover:bg-red-700 text-white text-xs font-semibold rounded-lg"
            data-testid="btn-liberar-habitacion">
            <LogOut className="w-3.5 h-3.5" /> Liberar habitación
          </button>
        </div>
      </div>
    </div>
  );
}

function ModalEstadoInfo({ hab, onClose }) {
  const est = hab.estado;
  const meta = ESTADO_META[est] || ESTADO_META.deshabilitada;
  const operador = hab.estado_operador;
  const nota = hab.estado_nota;
  const cuando = hab.estado_actualizado_at
    ? new Date(hab.estado_actualizado_at).toLocaleString('es-CL', {
        day: '2-digit', month: '2-digit', year: 'numeric',
        hour: '2-digit', minute: '2-digit',
      })
    : null;
  const iconAccent = est === 'aseo'
    ? 'from-amber-400 to-amber-600'
    : est === 'mantenimiento'
      ? 'from-orange-500 to-orange-700'
      : 'from-slate-500 to-slate-700';

  return (
    <div className="fixed inset-0 z-50 bg-black/50 flex items-center justify-center p-4" onClick={onClose}>
      <div className="bg-white rounded-2xl w-full max-w-md overflow-hidden shadow-2xl" onClick={e => e.stopPropagation()} data-testid="sg-estado-info-modal">
        <div className={`bg-gradient-to-r ${iconAccent} text-white px-5 py-4 flex items-center justify-between`}>
          <div>
            <div className="text-[11px] font-semibold uppercase tracking-wider opacity-90">Habitación · {meta.label}</div>
            <div className="text-2xl font-black leading-tight">{hab.numero}</div>
            <div className="text-xs opacity-90">{hab.edificio || 'Sin edificio'} · {hab.tipo || 'Sin tipo'}</div>
          </div>
          <button onClick={onClose} className="p-1 rounded hover:bg-white/20"><X className="w-5 h-5" /></button>
        </div>
        <div className="p-5 space-y-3">
          {operador ? (
            <div className="rounded-lg bg-slate-50 border border-slate-200 p-3 space-y-1">
              <div className="text-[10px] font-bold uppercase tracking-wide text-slate-500">
                {est === 'aseo' ? 'A cargo del aseo' : est === 'mantenimiento' ? 'A cargo de la mantención' : 'Última actualización'}
              </div>
              <div className="text-lg font-bold text-slate-900 flex items-center gap-2">
                <User className="w-4 h-4 text-emerald-700" />
                {operador}
              </div>
              {cuando && <div className="text-[11px] text-slate-500 ml-6">Reportado el {cuando}</div>}
            </div>
          ) : (
            <div className="rounded-lg bg-amber-50 border border-amber-200 p-3 text-sm text-amber-800 flex items-center gap-2">
              <AlertCircle className="w-4 h-4" />
              Sin operador registrado. El estado fue cambiado sin identificar quién lo realizó.
            </div>
          )}
          {nota && (
            <div className="rounded-lg bg-white border border-slate-200 p-3">
              <div className="text-[10px] font-bold uppercase tracking-wide text-slate-400 mb-1">Nota</div>
              <div className="text-sm text-slate-700 italic">&ldquo;{nota}&rdquo;</div>
            </div>
          )}
          <div className="text-[11px] text-slate-400 pt-1">
            Para modificar la habitación entra a <b>Mantenedores &rarr; Pabellones</b>.
          </div>
        </div>
      </div>
    </div>
  );
}

function Countdown({ targetDate, label }) {
  const [now, setNow] = useState(() => new Date());
  useEffect(() => {
    const id = setInterval(() => setNow(new Date()), 1000);
    return () => clearInterval(id);
  }, []);
  // targetDate viene en formato YYYY-MM-DD. Se interpreta como fin del día (23:59:59) en la zona
  // horaria del navegador. Como el usuario opera en Chile, coincide con America/Santiago.
  const target = new Date(`${targetDate}T23:59:59`);
  const diffMs = target - now;
  const expirado = diffMs <= 0;
  const abs = Math.abs(diffMs);
  const totalSec = Math.floor(abs / 1000);
  const dias = Math.floor(totalSec / 86400);
  const horas = Math.floor((totalSec / 3600) % 24);
  const minutos = Math.floor((totalSec / 60) % 60);
  const segundos = totalSec % 60;

  const tone = expirado
    ? 'from-red-600 to-red-800'
    : dias < 3
      ? 'from-amber-500 to-orange-600'
      : 'from-emerald-600 to-emerald-800';

  return (
    <div className={`bg-gradient-to-r ${tone} text-white px-5 py-3`} data-testid="sg-countdown">
      <div className="text-[10px] font-bold uppercase tracking-widest opacity-80">
        {label} · {targetDate.split('-').reverse().join('-')}
      </div>
      <div className="flex items-baseline gap-2 mt-1">
        <TimeUnit value={dias} label="días" />
        <span className="opacity-60 text-lg font-black">:</span>
        <TimeUnit value={String(horas).padStart(2, '0')} label="hrs" />
        <span className="opacity-60 text-lg font-black">:</span>
        <TimeUnit value={String(minutos).padStart(2, '0')} label="min" />
        <span className="opacity-60 text-lg font-black">:</span>
        <TimeUnit value={String(segundos).padStart(2, '0')} label="seg" />
        <span className="ml-auto text-[10px] font-semibold opacity-90 self-center">
          {expirado ? 'VENCIDO hace' : 'restan'}
        </span>
      </div>
    </div>
  );
}

function TimeUnit({ value, label }) {
  return (
    <div className="flex flex-col items-center">
      <span className="text-2xl font-black leading-none tabular-nums">{value}</span>
      <span className="text-[9px] uppercase tracking-widest opacity-80">{label}</span>
    </div>
  );
}

function InfoRow({ icon: Icon, label, value }) {
  return (
    <div className="flex items-start gap-2 text-sm">
      <Icon className="w-3.5 h-3.5 text-slate-500 mt-0.5 shrink-0" />
      <div className="min-w-0 flex-1">
        <div className="text-[10px] font-bold uppercase tracking-wide text-slate-400">{label}</div>
        <div className="text-slate-800 font-medium truncate">{value}</div>
      </div>
    </div>
  );
}

function TablaHabitaciones({ rows, onEditar, onEliminar }) {
  return (
    <div className="bg-white rounded-2xl border border-slate-200 overflow-hidden">
      <table className="w-full text-sm">
        <thead className="bg-slate-50 text-slate-500 text-xs">
          <tr>
            <th className="text-left px-4 py-2 font-semibold">N°</th>
            <th className="text-left px-4 py-2 font-semibold">Tipo</th>
            <th className="text-left px-4 py-2 font-semibold">Edificio</th>
            <th className="text-center px-4 py-2 font-semibold">Capacidad</th>
            <th className="text-center px-4 py-2 font-semibold">Estado</th>
            <th className="text-right px-4 py-2 font-semibold">Acciones</th>
          </tr>
        </thead>
        <tbody>
          {rows.map(h => {
            const est = estadoEfectivo(h);
            const meta = ESTADO_META[est];
            return (
              <tr key={h.id} className="border-t border-slate-100 hover:bg-slate-50/50">
                <td className="px-4 py-2 font-bold text-slate-800">{h.numero}</td>
                <td className="px-4 py-2 text-slate-600">{h.tipo || '—'}</td>
                <td className="px-4 py-2 text-slate-600">{h.edificio || '—'}</td>
                <td className="px-4 py-2 text-center">{h.capacidad}</td>
                <td className="px-4 py-2 text-center">
                  <span className={`inline-flex items-center gap-1.5 px-2 py-0.5 rounded-full text-[10px] font-bold ${meta.badge}`}>
                    <span className={`w-1.5 h-1.5 rounded-full ${meta.dot}`} />
                    {meta.label}
                  </span>
                </td>
                <td className="px-4 py-2 text-right">
                  <button onClick={() => onEditar(h)} className="p-1.5 text-slate-500 hover:text-teal-600 hover:bg-teal-50 rounded"><Pencil className="w-4 h-4" /></button>
                  <button onClick={() => onEliminar(h)} className="p-1.5 text-slate-500 hover:text-red-600 hover:bg-red-50 rounded"><Trash2 className="w-4 h-4" /></button>
                </td>
              </tr>
            );
          })}
        </tbody>
      </table>
    </div>
  );
}

function ModalHab({ modal, onClose, onSaved, headers }) {
  const isEdit = modal.mode === 'edit';
  const d = modal.data;
  const [form, setForm] = useState({
    numero: d?.numero || '',
    tipo: d?.tipo || '',
    edificio: d?.edificio || '',
    capacidad: d?.capacidad || 1,
    estado: d?.estado || 'activa',
    observaciones: d?.observaciones || '',
  });
  const [pabellones, setPabellones] = useState([]);
  const [showQr, setShowQr] = useState(false);
  const qrUrl = isEdit ? `${window.location.origin}/sg-qr/${encodeURIComponent(d.numero)}` : '';

  useEffect(() => {
    axios.get(`${API}/servicios-generales/pabellones`, { headers })
      .then(r => setPabellones(r.data || []))
      .catch(() => setPabellones([]));
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const submit = async (e) => {
    e.preventDefault();
    if (!form.numero) { toast.error('N° de habitación obligatorio'); return; }
    try {
      const payload = { ...form, capacidad: Number(form.capacidad) };
      if (isEdit) await axios.put(`${API}/servicios-generales/habitaciones/${d.id}`, payload, { headers });
      else await axios.post(`${API}/servicios-generales/habitaciones`, payload, { headers });
      toast.success('Guardado.');
      onSaved();
    } catch (err) { toast.error(err.response?.data?.detail || 'Error'); }
  };

  const descargarQr = () => {
    const canvas = document.getElementById('qr-hab-canvas');
    if (!canvas) return;
    const url = canvas.toDataURL('image/png');
    const a = document.createElement('a');
    a.href = url;
    a.download = `QR-Habitacion-${d.numero}.png`;
    a.click();
  };

  return (
    <div className="fixed inset-0 z-50 bg-black/50 flex items-center justify-center p-4 overflow-y-auto" onClick={onClose}>
      <div className="bg-white rounded-2xl p-6 max-w-lg w-full my-8" onClick={e => e.stopPropagation()}>
        <div className="flex items-center justify-between mb-4">
          <h3 className="text-lg font-bold">{isEdit ? 'Editar' : 'Nueva'} Habitación</h3>
          <button onClick={onClose}><X className="w-5 h-5" /></button>
        </div>
        <form onSubmit={submit} className="grid grid-cols-2 gap-3">
          <div><label className="text-xs text-slate-500 font-semibold">N° *</label>
            <input value={form.numero} onChange={e => setForm({ ...form, numero: e.target.value })} className="w-full px-3 py-2 text-sm border rounded-lg" data-testid="input-numero" /></div>
          <div><label className="text-xs text-slate-500 font-semibold">Capacidad</label>
            <input type="number" min="1" value={form.capacidad} onChange={e => setForm({ ...form, capacidad: e.target.value })} className="w-full px-3 py-2 text-sm border rounded-lg" /></div>
          <div><label className="text-xs text-slate-500 font-semibold">Tipo</label>
            <input value={form.tipo} onChange={e => setForm({ ...form, tipo: e.target.value })} placeholder="Individual, Doble..." className="w-full px-3 py-2 text-sm border rounded-lg" /></div>
          <div><label className="text-xs text-slate-500 font-semibold">Edificio / Pabellón</label>
            <input value={form.edificio} onChange={e => setForm({ ...form, edificio: e.target.value })}
              list="lista-pabellones"
              placeholder="Selecciona o escribe…"
              className="w-full px-3 py-2 text-sm border rounded-lg" />
            <datalist id="lista-pabellones">
              {pabellones.map(p => <option key={p.id} value={p.nombre}>{p.descripcion || ''}</option>)}
            </datalist>
            {pabellones.length === 0 && (
              <p className="text-[10px] text-slate-400 mt-1">
                Sin pabellones creados. Ve a <b>Pabellones</b> en el menú para crear el catálogo.
              </p>
            )}
          </div>
          <div className="col-span-2"><label className="text-xs text-slate-500 font-semibold">Estado</label>
            <select value={form.estado} onChange={e => setForm({ ...form, estado: e.target.value })} className="w-full px-3 py-2 text-sm border rounded-lg" data-testid="select-estado">
              <option value="activa">Activa (disponible u ocupada)</option>
              <option value="aseo">En aseo</option>
              <option value="mantenimiento">Mantenimiento</option>
              <option value="deshabilitada">Deshabilitada</option>
            </select>
            <p className="text-[10px] text-slate-400 mt-1">
              El estado &quot;Ocupada&quot; se calcula automáticamente cuando la habitación tiene una asignación activa.
            </p>
          </div>
          <div className="col-span-2"><label className="text-xs text-slate-500 font-semibold">Observaciones</label>
            <textarea value={form.observaciones} onChange={e => setForm({ ...form, observaciones: e.target.value })} rows={2} className="w-full px-3 py-2 text-sm border rounded-lg" /></div>

          {isEdit && (
            <div className="col-span-2 border-t border-slate-200 pt-3 mt-1">
              <button type="button" onClick={() => setShowQr(v => !v)}
                className="inline-flex items-center gap-2 text-sm font-semibold text-emerald-800 hover:text-emerald-900"
                data-testid="btn-toggle-qr">
                <QrCode className="w-4 h-4" /> {showQr ? 'Ocultar' : 'Mostrar'} código QR de la habitación
              </button>
              {showQr && (
                <div className="mt-3 flex flex-col items-center bg-slate-50 border border-slate-200 rounded-xl p-4">
                  <div className="text-[11px] font-bold uppercase tracking-widest text-slate-500 mb-2">Habitación {d.numero}</div>
                  <div className="bg-white p-3 rounded-lg shadow-sm border border-slate-200">
                    <QRCodeCanvas
                      id="qr-hab-canvas"
                      value={qrUrl}
                      size={200}
                      level="H"
                      includeMargin={false}
                    />
                  </div>
                  <div className="text-[11px] text-slate-500 mt-2 break-all text-center max-w-full px-2">{qrUrl}</div>
                  <button type="button" onClick={descargarQr}
                    className="inline-flex items-center gap-1.5 mt-3 px-3 py-1.5 bg-emerald-700 hover:bg-emerald-800 text-white text-xs font-semibold rounded-lg"
                    data-testid="btn-descargar-qr">
                    <Download className="w-3.5 h-3.5" /> Descargar PNG para imprimir
                  </button>
                  <p className="text-[10px] text-slate-400 mt-2 text-center max-w-xs">
                    Al escanear, el personal podrá cambiar el estado (aseo, mantenimiento, disponible) ingresando la clave configurada en <b>Reglas &rarr; Hotelería</b>.
                  </p>
                </div>
              )}
            </div>
          )}

          <div className="col-span-2 flex justify-end gap-2 mt-2">
            <button type="button" onClick={onClose} className="px-4 py-2 text-sm border rounded-lg">Cancelar</button>
            <button type="submit" className="px-4 py-2 text-sm bg-emerald-700 hover:bg-emerald-800 text-white rounded-lg font-semibold" data-testid="btn-guardar-hab">{isEdit ? 'Guardar' : 'Crear'}</button>
          </div>
        </form>
      </div>
    </div>
  );
}


function ModalAsignarTrabajador({ hab, headers, onClose, onSaved }) {
  const hoy = new Date().toISOString().slice(0, 10);
  const in14 = (() => { const d = new Date(); d.setDate(d.getDate() + 14); return d.toISOString().slice(0, 10); })();
  const [rut, setRut] = useState('');
  const [checking, setChecking] = useState(false);
  const [hab_info, setHabInfo] = useState(null);
  const [checkIn, setCheckIn] = useState(hoy);
  const [checkOut, setCheckOut] = useState(in14);
  const [obs, setObs] = useState('');
  const [enviando, setEnviando] = useState(false);

  const verificar = async () => {
    if (!rut.trim()) { toast.error('Ingresa un RUT'); return; }
    setChecking(true);
    setHabInfo(null);
    try {
      const r = await axios.get(`${API}/servicios-generales/verificar-habilitacion`, { params: { rut } });
      setHabInfo(r.data);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Error al verificar RUT');
    } finally {
      setChecking(false);
    }
  };

  const asignar = async () => {
    if (!hab_info?.hoteleria_habilitado) { toast.error('Trabajador no habilitado para hotelería'); return; }
    if (!checkIn || !checkOut) { toast.error('Check-in y check-out obligatorios'); return; }
    if (checkOut < checkIn) { toast.error('El check-out debe ser posterior al check-in'); return; }
    setEnviando(true);
    try {
      await axios.post(`${API}/servicios-generales/hoteleria/asignaciones`, {
        habitacion_id: hab.id,
        rut_trabajador: rut,
        check_in: checkIn,
        check_out: checkOut,
        observaciones: obs,
      }, { headers });
      toast.success(`Trabajador asignado a ${hab.numero}.`);
      onSaved();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Error al asignar');
    } finally {
      setEnviando(false);
    }
  };

  const esLomas = hab_info?.es_lomas;

  return (
    <div className="fixed inset-0 z-50 bg-black/50 flex items-center justify-center p-4 overflow-y-auto" onClick={onClose}>
      <div className="bg-white rounded-2xl w-full max-w-md overflow-hidden shadow-2xl my-8" onClick={e => e.stopPropagation()} data-testid="sg-asignar-modal">
        <div className="bg-gradient-to-r from-emerald-600 to-emerald-800 text-white px-5 py-4 flex items-center justify-between">
          <div>
            <div className="text-[11px] font-semibold uppercase tracking-wider opacity-90">Asignar trabajador</div>
            <div className="text-2xl font-black leading-tight">{hab.numero}</div>
            <div className="text-xs opacity-90">{hab.edificio || 'Sin edificio'} · {hab.tipo || 'Sin tipo'} · cap {hab.capacidad}</div>
          </div>
          <button onClick={onClose} className="p-1 rounded hover:bg-white/20"><X className="w-5 h-5" /></button>
        </div>

        <div className="p-5 space-y-4">
          <div>
            <label className="text-[11px] font-bold uppercase tracking-widest text-slate-500 mb-1 block">RUT del trabajador</label>
            <div className="flex gap-2">
              <input value={rut} onChange={e => { setRut(e.target.value); setHabInfo(null); }}
                onKeyDown={e => { if (e.key === 'Enter') verificar(); }}
                placeholder="12345678-9"
                className="flex-1 px-3 py-2 text-sm border border-slate-300 rounded-lg font-mono"
                data-testid="input-asignar-rut" />
              <button onClick={verificar} disabled={checking || !rut.trim()}
                className="inline-flex items-center gap-1.5 px-3 py-2 bg-emerald-700 hover:bg-emerald-800 text-white text-sm font-semibold rounded-lg disabled:opacity-50"
                data-testid="btn-verificar-rut">
                <Search className="w-4 h-4" /> {checking ? '…' : 'Verificar'}
              </button>
            </div>
          </div>

          {hab_info && (
            hab_info.encontrado ? (
              <div className={`rounded-lg border p-3 space-y-1 ${hab_info.hoteleria_habilitado ? 'bg-emerald-50 border-emerald-200' : 'bg-red-50 border-red-200'}`}>
                <div className="flex items-center gap-2">
                  {hab_info.hoteleria_habilitado
                    ? <CheckCircle2 className="w-4 h-4 text-emerald-700" />
                    : <AlertCircle className="w-4 h-4 text-red-600" />}
                  <span className={`text-sm font-bold ${hab_info.hoteleria_habilitado ? 'text-emerald-900' : 'text-red-800'}`}>
                    {hab_info.nombre}
                  </span>
                </div>
                <div className="text-[11px] text-slate-600 ml-6">
                  {hab_info.empresa_nombre || '—'}{esLomas ? ' · Lomas Bayas' : ''}
                </div>
                <div className={`text-[11px] ml-6 ${hab_info.hoteleria_habilitado ? 'text-emerald-800' : 'text-red-700 font-semibold'}`}>
                  {hab_info.motivo}
                </div>
              </div>
            ) : (
              <div className="rounded-lg bg-red-50 border border-red-200 p-3 flex items-center gap-2 text-red-800 text-sm">
                <AlertCircle className="w-4 h-4" /> {hab_info.motivo || 'Trabajador no encontrado'}
              </div>
            )
          )}

          {hab_info?.hoteleria_habilitado && (
            <>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="text-[11px] font-bold uppercase tracking-widest text-slate-500 mb-1 block">Check-in</label>
                  <input type="date" value={checkIn} onChange={e => setCheckIn(e.target.value)}
                    className="w-full px-3 py-2 text-sm border border-slate-300 rounded-lg"
                    data-testid="input-asignar-checkin" />
                </div>
                <div>
                  <label className="text-[11px] font-bold uppercase tracking-widest text-slate-500 mb-1 block">Check-out</label>
                  <input type="date" value={checkOut} min={checkIn} onChange={e => setCheckOut(e.target.value)}
                    className="w-full px-3 py-2 text-sm border border-slate-300 rounded-lg"
                    data-testid="input-asignar-checkout" />
                </div>
              </div>
              <div>
                <label className="text-[11px] font-bold uppercase tracking-widest text-slate-500 mb-1 block">Observaciones</label>
                <textarea value={obs} onChange={e => setObs(e.target.value)} rows={2}
                  placeholder="Notas opcionales…"
                  className="w-full px-3 py-2 text-sm border border-slate-300 rounded-lg" />
              </div>
            </>
          )}
        </div>

        <div className="px-5 py-3 bg-slate-50 border-t border-slate-200 flex justify-end items-center gap-2">
          <button onClick={onClose} className="px-3 py-1.5 text-xs border rounded-lg">Cancelar</button>
          <button onClick={asignar} disabled={!hab_info?.hoteleria_habilitado || enviando}
            className="inline-flex items-center gap-1.5 px-3 py-1.5 bg-emerald-700 hover:bg-emerald-800 text-white text-xs font-semibold rounded-lg disabled:opacity-40"
            data-testid="btn-confirmar-asignacion">
            <UserPlus className="w-3.5 h-3.5" /> {enviando ? 'Asignando…' : 'Asignar'}
          </button>
        </div>
      </div>
    </div>
  );
}
